import { getFirestore, doc, getDoc } from "firebase/firestore";
import { session } from "../state/session";
import { ensureUserDoc } from "../services/users";

/**
 * Ensures session.activeClubId is set (from localStorage first, then canonical Users/{uid}).
 * Returns the resolved clubId, or null if user has no clubs.
 */
export async function ensureActiveClub(): Promise<string | null> {
  // Make sure auth listener is attached and settled
  session.bootstrap();
  await session.ready();

  const uid = session.user?.uid;
  if (!uid) return null;

  // Prefer what we already have in memory/localStorage
  const existing = session.getActiveClubId();
  if (existing) return existing;

  // Create/backfill canonical user doc if needed (memberships -> Users/{uid})
  // NOTE: This is safe in normal pages, but DO NOT call ensureActiveClub from club-switcher.
  try {
    await ensureUserDoc();
  } catch {
    // If this fails (e.g. function unavailable), we still attempt to read Users/{uid}.
    // We'll just return null if it doesn't exist / has no clubs.
  }

  const db = getFirestore();
  const snap = await getDoc(doc(db, "Users", uid));
  if (!snap.exists()) return null;

  const data = snap.data() as any;

  // Canonical preference
  const activeClubId = typeof data.activeClubId === "string" && data.activeClubId.trim()
    ? data.activeClubId.trim()
    : null;

  if (activeClubId) {
    session.setActiveClubId(activeClubId);
    return activeClubId;
  }

  // Fallback: first club in index
  const clubIds = Array.isArray(data.clubIds) ? data.clubIds.map(String) : [];
  const first = clubIds.find(Boolean) ?? null;

  if (first) {
    session.setActiveClubId(first);
    return first;
  }

  return null;
}
